import React, { useEffect, useRef, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Col, Form, Modal, Row } from 'react-bootstrap'
import CreatableSelect from 'react-select/creatable'

export default function ReclassifyProviderModal({ show, onHide, provider, onConfirm, orgOptions = [] }) {
  const isSoleProprietor = provider?.classification === 'SoleProprietor'
  const defaultTarget = provider?.classification === 'Organization' ? 'Individual' : 'Organization'
  const [targetClassification, setTargetClassification] = useState(defaultTarget)

  // State for decomposing Org -> Individual
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [suffix, setSuffix] = useState('')
  const [selectedFacility, setSelectedFacility] = useState(null)

  // State for Individual / SoleProprietor -> Org
  const [organizationName, setOrganizationName] = useState('')

  const firstInputRef = useRef(null)

  useEffect(() => {
    if (!show || !provider) return
    setTargetClassification(provider.classification === 'Organization' ? 'Individual' : 'Organization')
  }, [show, provider])

  useEffect(() => {
    if (!show || !provider) return

    const rawName = (provider.legacyDisplayName || provider.name || '').trim()

    if (targetClassification === 'Individual') {
      // Deconstruct provider.name into first, last, suffix
      let f = provider.firstName || ''
      let l = provider.lastName || ''
      let s = provider.suffix || ''

      if (!f && !l && rawName) {
        const parts = rawName.split(/\s+/)
        const credentials = ['MD', 'DO', 'DC', 'PT', 'DPT', 'OD', 'NP', 'PA-C', 'PA', 'PhD', 'MS', 'FACS']
        const lastPart = parts[parts.length - 1].replace(/[.,]/g, '')
        const firstPart = parts[0].replace(/[.,]/g, '')

        if (credentials.some(c => c.toLowerCase() === lastPart.toLowerCase())) {
          s = parts[parts.length - 1].replace(/,/g, '')
          l = parts[parts.length - 2].replace(/,/g, '')
          f = parts.slice(0, parts.length - 2).join(' ')
        } else if (
          credentials.some(c => c.toLowerCase() === firstPart.toLowerCase()) ||
          firstPart.toLowerCase() === 'dr'
        ) {
          s = parts[0].replace(/,/g, '')
          if (parts.length === 2) {
            f = ''
            l = parts[1] || ''
          } else {
            f = parts[1] || ''
            l = parts.slice(2).join(' ')
          }
        } else if (parts.length >= 2) {
          f = parts[0]
          l = parts.slice(1).join(' ')
        } else {
          f = rawName
        }
      }

      setFirstName(f)
      setLastName(l)
      setSuffix(s)
      const existingOrgName = (provider.organizationName || '').trim()
      if (existingOrgName && existingOrgName.toLowerCase() !== rawName.toLowerCase()) {
        const matched = orgOptions.find(
          o =>
            o.id !== provider.id && (o.value || o.label || '').trim().toLowerCase() === existingOrgName.toLowerCase(),
        )
        setSelectedFacility(
          matched || {
            id: null,
            label: existingOrgName,
            value: existingOrgName,
          },
        )
      } else {
        setSelectedFacility(null)
      }
    } else {
      // Deconstruct Individual / SoleProprietor -> Org
      const combined = [provider.firstName, provider.lastName].filter(Boolean).join(' ')
      const withSuffix = provider.suffix ? `${combined}, ${provider.suffix}` : combined
      setOrganizationName(provider.organizationName || withSuffix || rawName)
    }
  }, [show, provider, targetClassification, orgOptions])

  const handleApply = () => {
    if (targetClassification === 'Individual') {
      const affiliations = []
      if (selectedFacility) {
        affiliations.push({
          organizationId: selectedFacility.id || null,
          organizationName: selectedFacility.value || selectedFacility.label,
          role: 'Primary Practice',
          isPrimary: true,
        })
      }

      onConfirm({
        targetClassification: 'Individual',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        suffix: suffix.trim(),
        organizationName: selectedFacility ? selectedFacility.value || selectedFacility.label : '',
        affiliations,
      })
    } else {
      onConfirm({
        targetClassification: 'Organization',
        organizationName: organizationName.trim(),
        firstName: '',
        lastName: '',
        suffix: '',
        affiliations: [],
      })
    }
    onHide()
  }

  const childClinicianCount = Array.isArray(provider?.individuals)
    ? provider.individuals.filter(
        i =>
          i &&
          (i.id || (i.firstName && i.firstName.trim()) || (i.lastName && i.lastName.trim()) || (i.npi && i.npi.trim())),
      ).length
    : 0

  return (
    <Modal
      show={show}
      onHide={onHide}
      onEntered={() => firstInputRef.current?.focus()}
      size="lg"
      backdrop="static"
      style={{ zIndex: 1065 }}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          Reclassify Provider: Convert to{' '}
          {targetClassification === 'Individual' ? 'Individual Practitioner' : 'Organization / Facility'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isSoleProprietor && (
          <div className="d-flex align-items-center justify-content-between mb-3 p-2 bg-light border rounded">
            <span className="small font-weight-bold text-muted">Convert Sole Proprietor record to:</span>
            <ButtonGroup size="sm">
              <Button
                type="button"
                variant={targetClassification === 'Organization' ? 'primary' : 'outline-secondary'}
                onClick={() => setTargetClassification('Organization')}
              >
                🏢 Organization / Facility
              </Button>
              <Button
                type="button"
                variant={targetClassification === 'Individual' ? 'primary' : 'outline-secondary'}
                onClick={() => setTargetClassification('Individual')}
              >
                👤 Individual Practitioner
              </Button>
            </ButtonGroup>
          </div>
        )}
        <Alert variant="warning" className="mb-3">
          <strong>Administrative Remediation:</strong> You are converting this record from{' '}
          <Badge variant="secondary">{provider?.classification || 'Organization'}</Badge> to{' '}
          <Badge variant="primary">{targetClassification}</Badge>. This updates how this provider is categorized in the
          Directory, Case Treatment lists, and Progress Reports.
        </Alert>

        {targetClassification === 'Individual' ? (
          <div>
            <p className="text-muted">
              We parsed the record name into first, last, and credentials below. Please review and adjust the name
              fields as needed.
            </p>

            <Row className="mb-3">
              <Col md={5}>
                <Form.Group controlId="reclassFirstName">
                  <Form.Label className="font-weight-bold">First Name</Form.Label>
                  <Form.Control
                    ref={firstInputRef}
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="First Name"
                  />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group controlId="reclassLastName">
                  <Form.Label className="font-weight-bold">Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Last Name"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId="reclassSuffix">
                  <Form.Label className="font-weight-bold">Suffix / Credential</Form.Label>
                  <Form.Control
                    type="text"
                    value={suffix}
                    onChange={e => setSuffix(e.target.value)}
                    placeholder="MD, PA-C..."
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="reclassFacility" className="mb-3">
              <Form.Label className="font-weight-bold">
                Affiliated Practicing Facility <span className="text-muted font-weight-normal">(Optional)</span>
              </Form.Label>
              <CreatableSelect
                isClearable
                placeholder="Search or enter organization / facility..."
                options={orgOptions}
                value={selectedFacility}
                onChange={val => setSelectedFacility(val)}
                onCreateOption={inputValue => {
                  const clean = (inputValue || '').trim()
                  if (clean) {
                    setSelectedFacility({ id: null, label: clean, value: clean })
                  }
                }}
              />
              <Form.Text className="text-muted">
                If this practitioner works under a clinic, hospital, or medical group, select or enter it here to
                automatically link them.
              </Form.Text>
            </Form.Group>

            {childClinicianCount > 0 && (
              <Alert variant="danger" className="small">
                ⚠️ <strong>Clinician Roster Notice:</strong> This facility currently has {childClinicianCount}{' '}
                clinician(s) associated with it. Converting this record to an Individual will detach those clinicians as
                independent practitioners.
              </Alert>
            )}
          </div>
        ) : (
          <div>
            <p className="text-muted">Specify the organization or facility name below.</p>

            <Form.Group controlId="reclassOrgName" className="mb-3">
              <Form.Label className="font-weight-bold">Organization / Facility Name</Form.Label>
              <Form.Control
                ref={firstInputRef}
                type="text"
                value={organizationName}
                onChange={e => setOrganizationName(e.target.value)}
                placeholder="Organization Name"
              />
            </Form.Group>

            <Alert variant="info" className="small">
              ℹ️ <strong>Affiliation Notice:</strong> Converting to an Organization will remove individual practitioner
              affiliations and allow this facility to have its own staff clinicians.
            </Alert>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={
            targetClassification === 'Individual' ? !firstName.trim() && !lastName.trim() : !organizationName.trim()
          }
        >
          Apply Reclassification
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
